/**
 * Order location card — shows delivery address (with optional apartment & instructions)
 * or pickup location (restaurant name, address, phone).
 */

import { MapPin, Phone, Store, Truck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { formatPhoneNumber } from '@/utils/formatting'
import type { DeliveryInfo } from '@/types/customer'
import type { OrderType } from '@/types/order'

interface OrderLocationCardProps {
  orderType: OrderType
  deliveryInfo: DeliveryInfo | null
  restaurantTitle: string | undefined
  locationAddress: string | null | undefined
  selectedLocationPhone: string | null | undefined
}

export function OrderLocationCard({
  orderType,
  deliveryInfo,
  restaurantTitle,
  locationAddress,
  selectedLocationPhone,
}: OrderLocationCardProps) {
  const { t } = useTranslation()
  const isDelivery = orderType === 'delivery'

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          {isDelivery ? (
            <>
              <Truck className="h-4 w-4" />
              {t('common:deliveryAddress')}
            </>
          ) : (
            <>
              <Store className="h-4 w-4" />
              {t('common:pickupLocation')}
            </>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isDelivery && deliveryInfo ? (
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">{deliveryInfo.street}</div>
              {deliveryInfo.apartmentNumber && (
                <div className="text-sm text-muted-foreground">
                  {t('common:apt', { number: deliveryInfo.apartmentNumber })}
                </div>
              )}
              <div className="text-sm text-muted-foreground">
                {deliveryInfo.city}, {deliveryInfo.state} {deliveryInfo.zipCode}
              </div>
              {deliveryInfo.deliveryInstructions && (
                <div className="text-sm text-muted-foreground mt-2 italic">
                  &quot;{deliveryInfo.deliveryInstructions}&quot;
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">{restaurantTitle || ''}</div>
              {locationAddress && (
                <div className="text-sm text-muted-foreground">
                  {locationAddress}
                </div>
              )}
              {selectedLocationPhone && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
                  <Phone className="h-3 w-3" />
                  {formatPhoneNumber(selectedLocationPhone)}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
